// Comprobación de la cámara antes de abrir sesión (ADR 0004 §8, producto/05 §7.1).
// Única decisión que toma el SDK: si la cámara parece virtual o inyectada, NO se abre sesión.
import { grayStats, meanAbsDiff, sha256Hex } from './crypto.js';
import type { CaptureTrackSettings } from './types.js';

/** Motivos de rechazo del guardián. */
export type GuardReason =
  | 'virtual_camera'
  | 'settings_incoherent'
  | 'getusermedia_patched'
  | 'flat_frame'
  | 'no_sensor_noise'
  | 'no_frames';

/** Lo que el guardián necesita de un `MediaStreamTrack`. */
export interface GuardTrack {
  label: string;
  getSettings(): MediaTrackSettings;
  getCapabilities?(): MediaTrackCapabilities;
}

/** Lo que el guardián necesita de cada `MediaDeviceInfo`. */
export interface GuardDevice {
  kind: string;
  label: string;
  deviceId: string;
}

/** Muestra de un fotograma: instante (µs) y gris 32×32. */
export interface GuardSample {
  t_mono: number;
  gray32: Uint8Array;
}

export interface GuardInput {
  track: GuardTrack;
  /** `enumerateDevices()` tras conceder el permiso (con etiquetas). */
  devices: readonly GuardDevice[];
  /** La función `getUserMedia` usada (se comprueba que es nativa). */
  getUserMedia: unknown;
  /** Devuelve el siguiente fotograma del stream. */
  sample: () => Promise<GuardSample>;
}

export interface GuardResult {
  ok: boolean;
  reason?: GuardReason;
  /** H(etiqueta en minúsculas, sin espacios extremos), hex. */
  cameraLabelHash: string;
  trackSettings: CaptureTrackSettings;
  /** Métricas del ruido (para la telemetría; sin datos personales). */
  metrics?: { sd_a: number; sd_b: number; diff: number };
}

export interface CameraGuardOptions {
  /** `virtual_camera_labels` de `GET /v1/capture/config` (y de la sesión). */
  virtualLabels: readonly string[];
  /** Separación mínima entre las dos muestras de ruido (ms). Por defecto 100. */
  noiseGapMs?: number;
  /** Media mínima de |Δgris| entre muestras. Por defecto 0,2. */
  minDiff?: number;
  /** Desviación típica mínima de cada muestra. Por defecto 1. */
  minSd?: number;
  /** Máximo de fotogramas a esperar para separar las muestras. Por defecto 120. */
  maxSamples?: number;
}

/** ¿Es una función nativa del navegador (no sustituida por JavaScript)? */
export function isNativeFunction(fn: unknown): boolean {
  if (typeof fn !== 'function') return false;
  try {
    return Function.prototype.toString.call(fn).includes('[native code]');
  } catch {
    return false;
  }
}

/** ¿Alguna etiqueta contiene un nombre de cámara virtual? (minúsculas, `includes`). */
export function matchesVirtualLabel(
  labels: readonly string[],
  virtual: readonly string[],
): boolean {
  const needles = virtual.map((v) => v.trim().toLowerCase()).filter(Boolean);
  return labels.some((l) => {
    const label = l.toLowerCase();
    return needles.some((n) => label.includes(n));
  });
}

/** Ajustes del track en el formato del contrato (deviceId solo como hash). */
export async function trackSettingsOf(track: GuardTrack): Promise<CaptureTrackSettings> {
  const s = track.getSettings();
  const out: CaptureTrackSettings = {
    width: Math.max(1, Math.round(s.width ?? 0)),
    height: Math.max(1, Math.round(s.height ?? 0)),
    frame_rate: s.frameRate && s.frameRate > 0 ? Math.round(s.frameRate * 1000) / 1000 : 30,
  };
  if (s.facingMode) out.facing_mode = String(s.facingMode).slice(0, 32);
  if (s.deviceId) out.device_id_hash = await sha256Hex(s.deviceId);
  return out;
}

/**
 * Guardián de la cámara.
 *
 * ```ts
 * const guard = new CameraGuard({ virtualLabels: config.virtual_camera_labels });
 * const r = await guard.check({ track, devices, getUserMedia, sample });
 * if (!r.ok) …  // no se crea sesión; Telemetry + evento `rejected`
 * ```
 */
export class CameraGuard {
  private readonly gapUs: number;
  private readonly minDiff: number;
  private readonly minSd: number;
  private readonly maxSamples: number;

  constructor(private readonly opts: CameraGuardOptions) {
    this.gapUs = (opts.noiseGapMs ?? 100) * 1000;
    this.minDiff = opts.minDiff ?? 0.2;
    this.minSd = opts.minSd ?? 1;
    this.maxSamples = opts.maxSamples ?? 120;
  }

  /** Ejecuta todas las comprobaciones en orden y se detiene en el primer fallo. */
  async check(input: GuardInput): Promise<GuardResult> {
    const { track } = input;
    const settings = track.getSettings();
    const deviceLabel =
      input.devices.find((d) => d.kind === 'videoinput' && d.deviceId === settings.deviceId)
        ?.label ?? '';
    const label = track.label || deviceLabel;
    const base = {
      cameraLabelHash: await sha256Hex(label.trim().toLowerCase()),
      trackSettings: await trackSettingsOf(track),
    };
    const fail = (reason: GuardReason, extra: Partial<GuardResult> = {}): GuardResult => ({
      ok: false,
      reason,
      ...base,
      ...extra,
    });

    if (matchesVirtualLabel([track.label, deviceLabel], this.opts.virtualLabels))
      return fail('virtual_camera');
    if (!this.coherent(track)) return fail('settings_incoherent');
    if (!isNativeFunction(input.getUserMedia)) return fail('getusermedia_patched');

    // Ruido de sensor: dos fotogramas separados ≥ 100 ms, ninguno plano y distintos entre sí.
    let a: GuardSample;
    let b: GuardSample;
    try {
      a = await input.sample();
      b = await input.sample();
      for (let n = 0; b.t_mono - a.t_mono < this.gapUs; n++) {
        if (n >= this.maxSamples) return fail('no_frames');
        b = await input.sample();
      }
    } catch {
      return fail('no_frames');
    }
    const sa = grayStats(a.gray32).sd;
    const sb = grayStats(b.gray32).sd;
    const diff = meanAbsDiff(a.gray32, b.gray32);
    const metrics = {
      sd_a: Math.round(sa * 100) / 100,
      sd_b: Math.round(sb * 100) / 100,
      diff: Math.round(diff * 1000) / 1000,
    };
    if (sa <= this.minSd || sb <= this.minSd) return fail('flat_frame', { metrics });
    if (diff <= this.minDiff) return fail('no_sensor_noise', { metrics });
    return { ok: true, ...base, metrics };
  }

  /** Ancho/alto > 0 y `frameRate` 5–120; si hay capacidades, los ajustes caben en ellas. */
  private coherent(track: GuardTrack): boolean {
    const s = track.getSettings();
    if (!(s.width && s.width > 0 && s.height && s.height > 0)) return false;
    if (!(s.frameRate && s.frameRate >= 5 && s.frameRate <= 120)) return false;
    const caps = track.getCapabilities?.();
    if (caps) {
      const within = (v: number, r?: { min?: number; max?: number }) =>
        !r || ((r.min === undefined || v >= r.min) && (r.max === undefined || v <= r.max));
      if (!within(s.width, caps.width) || !within(s.height, caps.height)) return false;
      if (caps.width?.max === 0 || caps.height?.max === 0) return false;
    }
    return true;
  }
}
